/**
 * VisualLifecycleStore — singleton event buffer for combat / VFX diagnostics.
 *
 * Records every visual event (projectile spawn, impact VFX, FCT, screen shake,
 * sprite flash, etc.) with a deduplication key so the VisualLifecycleMonitor
 * can surface double-fire bugs.
 *
 * Primary use case: debugging why projectile impact VFX sometimes play twice.
 * Typical root cause: optimistic client path (combat:damage → onDamageDealt)
 * AND server-confirmed path (npcDamaged → DamagePresentationService →
 * CombatFeedbackManager.triggerImpact) both fire the same hit effect.
 */

export type VisualEventType =
  | 'projectile_spawn'
  | 'projectile_impact'
  | 'ground_impact_vfx'
  | 'hit_vfx'
  | 'impact_feedback'
  | 'fct_damage'
  | 'fct_heal'
  | 'screen_shake'
  | 'hitstop'
  | 'sprite_flash'
  | 'hit_feel'
  | 'chain_vfx'
  | 'aoe_vfx'
  | 'ability_cast_vfx'
  | 'status_vfx'
  | 'target_status_pips'
  | 'reaction_vfx'
  | 'death_vfx'
  | 'ground_mark'
  | 'atmos_pulse'
  | 'trail_vfx'
  // A7-ZONES: render-truth witness that a persistent zone's SCHEDULED stop
  // fired at duration end (VFXSystem `vfx:summon_expired` route). Positive
  // evidence a leaking zone cannot fake — the verifier's `zone-expires` check
  // reads it by ability. Derived from the `summon_<abilityId>_<ts>` instanceId.
  | 'zone_expired';

export interface VisualEvent {
  id: string;
  timestamp: number;
  type: VisualEventType;
  /** Human-readable label for the UI */
  label: string;
  /** Set by the store when a duplicate is detected */
  isDuplicate?: boolean;
  /** Reference to the original event if this is a duplicate */
  duplicateOf?: string;
  abilityId?: string | null;
  targetId?: string | null;
  projectileId?: string | null;
  /** Damage school / feedback type (arcane, physical, fire…) when known. */
  school?: string | null;
  /**
   * Which pipeline path emitted this event. Lets the dedup tell the
   * optimistic client splash apart from the server-confirmed one, and
   * lets the digest show "who fired this":
   *   'optimistic'  — client prediction (VFXSystem.onDamageDealt)
   *   'confirmed'   — server-authoritative (DamagePresentationService)
   *   'projectile'  — ProjectileSystem spawn/impact
   */
  source?: string | null;
  x: number;
  y: number;
  /** Extra metadata for debugging */
  meta?: Record<string, unknown>;
}

interface DedupRule {
  name: string;
  types: VisualEventType[];
  windowMs: number;
  keyFn: (e: VisualEvent) => string | null;
  /**
   * When true, only flag a duplicate if the prior event came from a
   * DIFFERENT source. This is the optimistic-vs-confirmed double-fire the
   * store exists to catch, while NOT flagging rapid same-source auto
   * swings (which legitimately repeat the same key).
   */
  crossSourceOnly?: boolean;
}

const DEDUP_RULES: DedupRule[] = [
  // The canonical double-fire: the SAME effect type for the same
  // target/ability/school rendered by two DIFFERENT pipeline paths
  // (optimistic client + server-confirmed) within one RTT. Keying on
  // `type` + `school` means complementary effects of one hit (fct vs
  // impact vs hit_vfx) and distinct schools of one swing (arcane vs
  // physical) are NOT cross-flagged; `crossSourceOnly` means rapid
  // same-source auto swings aren't either. 700ms covers a slow RTT.
  {
    name: 'cross-path-duplicate',
    types: ['hit_vfx', 'impact_feedback', 'ground_impact_vfx', 'fct_damage'],
    windowMs: 700,
    crossSourceOnly: true,
    keyFn: (e) => {
      if (!e.targetId) return null;
      const ability = e.abilityId || 'none';
      const school = e.school || 'none';
      return `${e.type}:${e.targetId}:${ability}:${school}`;
    },
  },
  // Fallback for effects WITHOUT a targetId (ground / AoE): same effect
  // type at the same spot from a different path within one RTT.
  {
    name: 'cross-path-duplicate-by-location',
    types: ['hit_vfx', 'impact_feedback', 'ground_impact_vfx', 'aoe_vfx'],
    windowMs: 700,
    crossSourceOnly: true,
    keyFn: (e) => {
      if (e.targetId) return null; // target-keyed rule already handles these
      const ability = e.abilityId || 'none';
      const school = e.school || 'none';
      const gx = Math.round(e.x / 8);
      const gy = Math.round(e.y / 8);
      return `${e.type}:${ability}:${school}:${gx}:${gy}`;
    },
  },
  // Same projectile impact twice = bug in projectile system
  {
    name: 'same-projectile-impact',
    types: ['projectile_impact'],
    windowMs: 50,
    keyFn: (e) => {
      if (!e.projectileId || !e.targetId) return null;
      return `proj:${e.projectileId}:${e.targetId}`;
    },
  },
  // Same screen shake within 100ms = redundant feedback
  {
    name: 'same-screen-shake',
    types: ['screen_shake'],
    windowMs: 100,
    keyFn: (e) => {
      const intensity = (e.meta?.intensity as number) || 0;
      return `shake:${Math.round(intensity)}`;
    },
  },
  // Same sprite flash on same target within 80ms
  {
    name: 'same-sprite-flash',
    types: ['sprite_flash', 'hit_feel'],
    windowMs: 80,
    keyFn: (e) => {
      if (!e.targetId) return null;
      return `flash:${e.targetId}`;
    },
  },
];

const MAX_EVENTS = 200;
const EVENTS: VisualEvent[] = [];

let listeners: Array<() => void> = [];
let dedupIndex = new Map<string, { id: string; timestamp: number; source?: string | null }>();

function genId(): string {
  return `vle_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

function checkDuplicate(event: VisualEvent): { isDuplicate: boolean; duplicateOf?: string } {
  for (const rule of DEDUP_RULES) {
    if (!rule.types.includes(event.type)) continue;
    const key = rule.keyFn(event);
    if (!key) continue;

    const prior = dedupIndex.get(key);
    if (prior && event.timestamp - prior.timestamp <= rule.windowMs) {
      // Cross-source rules only fire when a DIFFERENT path re-rendered the
      // same key — same-source recurrence (rapid auto swings) is legit.
      if (rule.crossSourceOnly && (prior.source ?? null) === (event.source ?? null)) continue;
      return { isDuplicate: true, duplicateOf: prior.id };
    }
  }
  return { isDuplicate: false };
}

function updateDedupIndex(event: VisualEvent): void {
  for (const rule of DEDUP_RULES) {
    if (!rule.types.includes(event.type)) continue;
    const key = rule.keyFn(event);
    if (!key) continue;
    dedupIndex.set(key, { id: event.id, timestamp: event.timestamp, source: event.source ?? null });
  }
}

function sweepOldDedupIndex(): void {
  const now = Date.now();
  const maxWindow = Math.max(...DEDUP_RULES.map((r) => r.windowMs));
  for (const [key, entry] of dedupIndex.entries()) {
    if (now - entry.timestamp > maxWindow * 2) {
      dedupIndex.delete(key);
    }
  }
}

function notify(): void {
  for (const cb of listeners) {
    try { cb(); } catch { /* nop */ }
  }
}

export const VisualLifecycleStore = {
  record(params: {
    type: VisualEventType;
    label: string;
    x: number;
    y: number;
    abilityId?: string | null;
    targetId?: string | null;
    projectileId?: string | null;
    school?: string | null;
    source?: string | null;
    meta?: Record<string, unknown>;
  }): VisualEvent {
    const id = genId();
    const timestamp = Date.now();

    const event: VisualEvent = {
      id,
      timestamp,
      type: params.type,
      label: params.label,
      x: params.x,
      y: params.y,
      abilityId: params.abilityId ?? null,
      targetId: params.targetId ?? null,
      projectileId: params.projectileId ?? null,
      school: params.school ?? null,
      source: params.source ?? null,
      meta: params.meta,
    };

    const dup = checkDuplicate(event);
    if (dup.isDuplicate) {
      event.isDuplicate = true;
      event.duplicateOf = dup.duplicateOf;
    }

    EVENTS.push(event);
    if (EVENTS.length > MAX_EVENTS) {
      EVENTS.shift();
    }

    updateDedupIndex(event);
    sweepOldDedupIndex();
    notify();

    return event;
  },

  all(): readonly VisualEvent[] {
    return EVENTS;
  },

  latest(n: number = 20): readonly VisualEvent[] {
    return EVENTS.slice(-n);
  },

  duplicates(): readonly VisualEvent[] {
    return EVENTS.filter((e) => e.isDuplicate);
  },

  countByType(): Record<VisualEventType, number> {
    const counts = {} as Record<VisualEventType, number>;
    for (const e of EVENTS) {
      counts[e.type] = (counts[e.type] || 0) + 1;
    }
    return counts;
  },

  duplicateCount(): number {
    return EVENTS.filter((e) => e.isDuplicate).length;
  },

  subscribe(cb: () => void): () => void {
    listeners.push(cb);
    return () => {
      listeners = listeners.filter((l) => l !== cb);
    };
  },

  clear(): void {
    EVENTS.length = 0;
    dedupIndex.clear();
    notify();
  },
};
